import { type ManualTask, ManualTaskStatus } from '@compass/client/types/manual_task';
import { manualTaskService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { paginateCursorList } from '@/utils/paginateCursorList';

const MAX_PAGES = 5;
const PAGE_SIZE = 100;

/**
 * Every manual task on a case (any assignee, any status), oldest first. Filtering
 * happens client-side in the Tasks sidebar so its chip counts agree.
 */
export function useCaseTasks(caseRecordId: string, enabled = true) {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  const query = useQuery({
    queryKey: queryKeys.caseTasks.byCase(caseRecordId),
    queryFn: async () => {
      const { records } = await paginateCursorList(
        async cursor => {
          const res = await manualTaskService.listCaseManualTasks({
            organization_id: organizationId!,
            program_id: programId!,
            case_record_id: caseRecordId,
            limit: PAGE_SIZE,
            start_after_id: cursor,
          });
          return { records: res.manual_tasks, next_cursor: res.next_cursor };
        },
        MAX_PAGES,
        PAGE_SIZE,
      );
      // The API pages newest first by ObjectID, which is creation order.
      return records.reverse();
    },
    enabled: enabled && !!caseRecordId && !!organizationId && !!programId,
    staleTime: 30 * 1000,
  });

  return { ...query, tasks: query.data ?? [] };
}

const STATUS_TOASTS: Partial<Record<ManualTaskStatus, string>> = {
  [ManualTaskStatus.MANUAL_TASK_STATUS_STARTED]: 'Task started',
  [ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED]: 'Task completed',
};

/**
 * Moves a task to `status` through the program-level UpdateManualTask API. The model only allows
 * pending -> started and started -> completed, so the sidebar starts a task before it can be
 * completed. `notes`, when given, are recorded as output.notes.
 */
export function useUpdateManualTaskStatus(caseRecordId: string) {
  const queryClient = useQueryClient();
  const organizationId = useStore(state => state.selectedOrganizationId);

  return useMutation({
    mutationFn: ({
      task,
      status,
      notes,
    }: {
      task: ManualTask;
      status: ManualTaskStatus;
      notes?: string;
    }) =>
      manualTaskService.updateManualTask({
        organization_id: organizationId!,
        // The task's own program: the server 404s a task outside the path program.
        program_id: task.program_id!,
        manual_task_id: task.id!,
        status,
        // UpdateManualTask replaces output wholesale; keep what the workflow wrote.
        ...(notes !== undefined && { output: { ...task.output, notes } }),
      }),
    onSuccess: (_, { status }) => {
      toast.success(STATUS_TOASTS[status] ?? 'Task updated');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update task');
    },
    // Refetch on failure too: a rejected update usually means the task changed server-side
    // (cancelled, already completed), and the card should show that instead of the stale state.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.caseTasks.byCase(caseRecordId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.myTasks.all });
    },
  });
}
